const OPENSTREETMAP_TILE_HOST_PATTERN =
  /^https?:\/\/(?:[abc]\.|\{s\}\.)?tile\.openstreetmap\.org(?=\/)/i;

export type TileLayerConfigEntry = {
  title?: unknown;
  tileLayer?: unknown;
  attribution?: unknown;
  options?: unknown;
};

export const OPENSTREETMAP_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
export const OPENSTREETMAP_TILE_PROXY_PATH = "/api/map/tiles/openstreetmap/{z}/{x}/{y}.png";
export const DEFAULT_MAP_TILE_LAYERS: TileLayerConfigEntry[] = [
  {
    title: "OpenStreetMap",
    tileLayer: OPENSTREETMAP_TILE_URL,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
  },
  {
    title: "OpenTopoMap",
    tileLayer: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, SRTM | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
  },
  {
    title: "Esri World Street",
    tileLayer: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
    attribution: "Sources: Esri, HERE, Garmin, USGS, OpenStreetMap contributors, and the GIS User Community"
  },
  {
    title: "Esri World Topographic",
    tileLayer: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
    attribution: "Sources: Esri, HERE, Garmin, USGS, OpenStreetMap contributors, and the GIS User Community"
  },
  {
    title: "Esri WorldImagery",
    tileLayer: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Source: Esri, Vantor, Earthstar Geographics, and the GIS User Community"
  },
  {
    title: "Offline Map",
    tileLayer: "offline-maps/{z}/{x}/{y}.png",
    attribution: "Offline cache"
  }
];

export const DEFAULT_MAP_TILE_LAYERS_JSON = JSON.stringify(DEFAULT_MAP_TILE_LAYERS);

export const resolveTileLayersJson = (value?: string | null): string => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return DEFAULT_MAP_TILE_LAYERS_JSON;
  }
  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) && parsed.length > 0 ? trimmed : DEFAULT_MAP_TILE_LAYERS_JSON;
  } catch {
    return DEFAULT_MAP_TILE_LAYERS_JSON;
  }
};

const isAbsoluteUrl = (value: string): boolean => /^[a-z][a-z0-9+.-]*:\/\//i.test(value);

export const appendMapTileAccessToken = (value: string, accessToken = ""): string => {
  const token = accessToken.trim();
  if (!token) {
    return value;
  }
  const separator = value.includes("?") ? "&" : "?";
  return `${value}${separator}access_token=${encodeURIComponent(token)}`;
};

export const buildOpenStreetMapTileUrl = (baseOrigin: string, accessToken = ""): string => {
  const base = baseOrigin.replace(/\/+$/, "");
  return appendMapTileAccessToken(`${base}${OPENSTREETMAP_TILE_PROXY_PATH}`, accessToken);
};

export const normalizeTileLayerUrl = (value: string, baseOrigin: string, accessToken = ""): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return trimmed;
  }

  // Route all current and legacy OSM endpoints through SkyView so the
  // upstream request has an application User-Agent and can be cached.
  if (OPENSTREETMAP_TILE_HOST_PATTERN.test(trimmed)) {
    return buildOpenStreetMapTileUrl(baseOrigin, accessToken);
  }

  if (/^https?:\/\/wails\.localhost(?::\d+)?\//i.test(trimmed)) {
    return trimmed.replace(/^https?:\/\/wails\.localhost(?::\d+)?/i, baseOrigin.replace(/\/+$/, ""));
  }

  if (isAbsoluteUrl(trimmed)) {
    return trimmed;
  }

  const base = baseOrigin.replace(/\/+$/, "");
  const path = trimmed.replace(/^\/+/, "");
  return `${base}/${path}`;
};

export const sanitizeTileLayersJson = (
  rawValue: string | null,
  baseOrigin: string | null,
  accessToken = ""
): string | null => {
  if (!rawValue) {
    return null;
  }
  if (!baseOrigin) {
    return rawValue;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawValue);
  } catch {
    return rawValue;
  }

  if (!Array.isArray(parsed)) {
    return rawValue;
  }

  const normalized = parsed.map((entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      return entry;
    }
    const record = { ...(entry as TileLayerConfigEntry) };
    if (typeof record.tileLayer === "string") {
      record.tileLayer = normalizeTileLayerUrl(record.tileLayer, baseOrigin, accessToken);
    }
    return record;
  });

  try {
    return JSON.stringify(normalized);
  } catch {
    return rawValue;
  }
};
